import { useEffect, useRef, useState } from 'react';

// Static class names so Tailwind can see them.
const LINE_CLAMP = { 3: 'line-clamp-3', 4: 'line-clamp-4', 5: 'line-clamp-5', 6: 'line-clamp-6' } as const;

// Long reviews start clamped to four lines. The toggle appears only when the
// clamp is actually hiding text, measured from the DOM.
export function ReviewText({
  text,
  className = 'text-sm leading-relaxed text-foreground/80',
  lines = 4,
  reserveToggle = false,
}: {
  text: string;
  className?: string;
  /** Visible lines before the clamp; 3 to 6 are supported. */
  lines?: 3 | 4 | 5 | 6;
  /** Keep the toggle's row even when nothing is hidden, so sibling cards line up. */
  reserveToggle?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const ref = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      if (open) return;
      setOverflows(el.scrollHeight > el.clientHeight + 1);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text, open]);

  return (
    <div className="mt-2">
      <p ref={ref} className={`${className} ${open ? '' : LINE_CLAMP[lines]}`}>
        {text}
      </p>
      {(overflows || open || reserveToggle) && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={`mt-1.5 text-xs font-semibold text-primary hover:underline ${overflows || open ? '' : 'invisible'}`}
          tabIndex={overflows || open ? 0 : -1}
          aria-hidden={!(overflows || open)}
        >
          {open ? 'Show less' : 'Read more'}
        </button>
      )}
    </div>
  );
}
